import { Input } from "@legal-500/legal500-design-system";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { Question } from "../../api/powerlist.service";
import pickerStyles from "../../components/CompanyPicker/CompanyPicker.module.css";
import styles from "./Powerlist.module.css";

export const QUESTION_RESULT_LIMIT = 50;

const INPUT_ID = "powerlist-edition-question";
const LISTBOX_ID = "powerlist-edition-question-listbox";

type QuestionPickerProps = {
  questions: Question[];
  value: string;
  query: string;
  onQueryChange: (query: string) => void;
  onChange: (questionId: string) => void;
};

export function QuestionPicker({
  questions,
  value,
  query,
  onQueryChange,
  onChange,
}: QuestionPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const selected = questions.find(question => question.id === value) ?? null;
  const term = selected && query === selected.text ? "" : query.trim().toLowerCase();
  const matches = term
    ? questions.filter(question => question.text.toLowerCase().includes(term))
    : questions;
  const results = matches.slice(0, QUESTION_RESULT_LIMIT);
  const optionId = (index: number) => `${LISTBOX_ID}-option-${index}`;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeIndex < 0) return;
    const option = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    option?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  function open() {
    setIsOpen(true);
    setActiveIndex(-1);
  }

  function handleInputChange(next: string) {
    onQueryChange(next);
    if (value) onChange("");
    open();
  }

  function handleSelect(question: Question) {
    onChange(question.id);
    onQueryChange(question.text);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        open();
        return;
      }
      if (results.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(current =>
        current < 0 && step < 0
          ? results.length - 1
          : (current + step + results.length) % results.length,
      );
    } else if (event.key === "Enter" && isOpen) {
      event.preventDefault();
      const active = results[activeIndex];
      if (active) handleSelect(active);
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
    } else if (event.key === "Tab") {
      setIsOpen(false);
    }
  }

  const activeResult = isOpen ? results[activeIndex] : undefined;

  return (
    <div className={pickerStyles.picker} ref={containerRef}>
      <Input
        id={INPUT_ID}
        label="Question from the bank"
        value={query}
        onChange={event => handleInputChange(event.target.value)}
        onFocus={open}
        onClick={() => !isOpen && open()}
        onKeyDown={handleKeyDown}
        title={selected?.text}
        fullWidth
        autoComplete="off"
        placeholder={questions.length ? "Search questions…" : "No more questions"}
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={LISTBOX_ID}
        aria-activedescendant={activeResult ? optionId(activeIndex) : undefined}
      />
      {isOpen && (
        <ul
          id={LISTBOX_ID}
          ref={listRef}
          role="listbox"
          aria-label="Questions"
          className={pickerStyles.dropdown}
        >
          {results.length === 0 && (
            <li className={pickerStyles.dropdownStatus} aria-live="polite">
              No matching questions
            </li>
          )}
          {results.map((question, index) => (
            <li
              key={question.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              title={question.text}
              className={`${pickerStyles.dropdownItem} ${styles.questionOption} ${
                index === activeIndex ? styles.questionOptionActive : ""
              }`}
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => handleSelect(question)}
            >
              {question.text}
            </li>
          ))}
          {matches.length > results.length && (
            <li className={pickerStyles.dropdownStatus}>
              Showing {results.length} of {matches.length}. Keep typing to narrow the list.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
